import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { X, Package, Ruler, FileText, CheckCircle2 } from 'lucide-react';
import { ShopTypeTemplate, UnitType, CustomProductField, templateService } from '../../services/configurationService';
import { getShopIcon } from '../../utils/iconMapper';
import { customToast } from '../../utils/customToast';

interface TemplatePreviewModalProps {
  templateId: number;
  onClose: () => void;
  onApply: (replaceExisting: boolean) => void;
}

const TemplatePreviewModal: React.FC<TemplatePreviewModalProps> = ({
  templateId,
  onClose,
  onApply,
}) => {
  const { theme } = useTheme();
  const [template, setTemplate] = useState<ShopTypeTemplate | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTemplateDetails();
  }, [templateId]);

  const loadTemplateDetails = async () => {
    try {
      setLoading(true);
      const data = await templateService.getById(templateId);
      setTemplate(data);
    } catch (error) {
      customToast.error('Failed to load template details');
      console.error('Error loading template:', error);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleApply = () => {
    onApply(true); // Always replace existing configuration
  };

  return createPortal(
    <div 
      className={`fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-[9999] animate-fadeIn`}
      onClick={onClose}
    >
      <div
        className={`w-full max-w-3xl max-h-[90vh] flex flex-col rounded-xl shadow-2xl animate-scaleIn ${
          theme === 'dark' ? 'bg-slate-800' : 'bg-white'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`px-4 sm:px-5 md:px-6 py-3 sm:py-4 border-b flex-shrink-0 rounded-t-xl ${
          theme === 'dark' ? 'border-slate-700 bg-slate-800/95' : 'border-slate-200 bg-gray-50/95'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
              {template?.icon && (
                <div className="text-blue-600 dark:text-blue-400 flex-shrink-0">
                  {getShopIcon(template.icon, 24)}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <h2 className={`text-sm sm:text-base md:text-lg font-bold truncate ${
                  theme === 'dark' ? 'text-white' : 'text-slate-900'
                }`}>
                  {template?.name}
                </h2>
                {template?.description && (
                  <p className={`text-xs truncate ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    {template.description}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className={`p-2 rounded-lg transition-colors touch-manipulation flex-shrink-0 ${
                theme === 'dark'
                  ? 'hover:bg-gray-700 text-gray-400'
                  : 'hover:bg-gray-100 text-gray-600'
              }`}
            >
              <X className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className={`flex-1 overflow-y-auto ${theme === 'dark' ? 'bg-slate-800' : 'bg-white'}`}>
          <div className="p-4 sm:p-5 md:p-6">
        {loading ? (
          <div className="flex justify-center items-center py-8 sm:py-12">
            <div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="space-y-4 sm:space-y-6">
              {/* Categories Section */}
              <div>
                <div className="flex items-center space-x-1.5 sm:space-x-2 mb-3 sm:mb-4">
                  <Package
                    className={`w-4 h-4 sm:w-5 sm:h-5 ${
                      theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                    }`}
                  />
                  <h3 className={`text-sm sm:text-base md:text-lg font-semibold ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                    Categories ({template?.categories_count || 0})
                  </h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5 sm:gap-2">
                  {template?.categories?.map((category, index) => (
                    <div
                      key={index}
                      className={`flex items-center space-x-1.5 sm:space-x-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg ${
                        theme === 'dark'
                          ? 'bg-gray-700 text-gray-200'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-500 flex-shrink-0" />
                      <span className="text-xs sm:text-sm truncate">{category}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Units Section */}
              <div>
                <div className="flex items-center space-x-1.5 sm:space-x-2 mb-3 sm:mb-4">
                  <Ruler
                    className={`w-4 h-4 sm:w-5 sm:h-5 ${
                      theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                    }`}
                  />
                  <h3 className={`text-sm sm:text-base md:text-lg font-semibold ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                    Unit Types ({template?.units_count || 0})
                  </h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-1.5 sm:gap-2">
                  {template?.units?.map((unit, index) => (
                    <div
                      key={index}
                      className={`flex items-center space-x-1.5 sm:space-x-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg ${
                        theme === 'dark'
                          ? 'bg-gray-700 text-gray-200'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-500 flex-shrink-0" />
                      <span className="text-xs sm:text-sm truncate">
                        {typeof unit === 'string' ? unit : (unit as UnitType).name || (unit as UnitType).short_name}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Custom Fields Section (if any) */}
              {template?.custom_fields && template.custom_fields.length > 0 && (
                <div>
                  <div className="flex items-center space-x-1.5 sm:space-x-2 mb-3 sm:mb-4">
                    <FileText
                      className={`w-4 h-4 sm:w-5 sm:h-5 ${
                        theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                      }`}
                    />
                    <h3 className={`text-sm sm:text-base md:text-lg font-semibold ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                      Custom Fields ({template.custom_fields_count || 0})
                    </h3>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-1.5 sm:gap-2">
                    {template.custom_fields.map((field, index) => (
                      <div
                        key={index}
                        className={`flex items-center space-x-1.5 sm:space-x-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg ${
                          theme === 'dark'
                            ? 'bg-gray-700 text-gray-200'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-500 flex-shrink-0" />
                        <span className="text-xs sm:text-sm truncate">
                          {typeof field === 'string' ? field : (field as CustomProductField).field_name || (field as CustomProductField).field_key}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Info Message */}
              <div className={`p-2.5 sm:p-3 md:p-4 rounded-lg border ${
                theme === 'dark'
                  ? 'bg-blue-900/20 border-blue-800'
                  : 'bg-blue-50 border-blue-200'
              }`}>
                <div className="flex items-start space-x-2 sm:space-x-3">
                  <CheckCircle2 className={`w-4 h-4 sm:w-5 sm:h-5 mt-0.5 flex-shrink-0 ${
                    theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                  }`} />
                  <div>
                    <p className={`text-xs sm:text-sm ${
                      theme === 'dark' ? 'text-blue-300' : 'text-blue-800'
                    }`}>
                      <strong>Note:</strong> Applying this template will replace your current configuration. 
                      Old categories and units will be removed, and template items will be set up for your shop.
                    </p>
                  </div>
                </div>
              </div>
          </div>
        )}
        </div>
        </div>

        {/* Footer */}
        <div className={`px-4 sm:px-5 md:px-6 py-3 sm:py-4 border-t flex-shrink-0 rounded-b-xl ${
          theme === 'dark' ? 'border-slate-700 bg-slate-800/95' : 'border-slate-200 bg-gray-50/95'
        }`}>
          <div className="flex flex-col-reverse sm:flex-row gap-3">
            <button
              onClick={onClose}
              className={`w-full sm:flex-1 px-4 py-2.5 text-sm font-medium rounded-xl border transition-all duration-200 touch-manipulation ${
                theme === 'dark'
                  ? 'border-slate-600 text-slate-300 hover:bg-slate-700 hover:border-slate-500'
                  : 'border-slate-300 text-slate-700 hover:bg-gray-100'
              }`}
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              disabled={loading}
              className="w-full sm:flex-1 px-4 py-2.5 text-sm font-medium text-white rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation bg-blue-500 hover:bg-blue-600"
            >
              Apply Template
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default TemplatePreviewModal;
